import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useForm, type Resolver } from 'react-hook-form';
import { type QueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';

import { useNavigateBack } from '#apps/client-web/app/hooks/use-navigate-back';
import { routes } from '#apps/client-web/app/routes';
import { type AuditFormService } from '#apps/client-web/features/audit-form/services/audit-form';
import { useLoadAuditFormByType } from '#apps/client-web/features/audit-form/hooks/use-load-audit-form-by-type-query';
import { useSaveAuditFormMutation } from '#apps/client-web/features/audit-form/hooks/use-save-audit-form-mutation';
import { AUDIT_FORMS_QUERY_KEY } from '#apps/client-web/features/audit-form/types/react-query';
import {
  type FileContent,
  type FileStorageService,
} from '#apps/client-web/features/file-storage/services/file-storage';
import { useLoadFilesQuery } from '#apps/client-web/features/file-storage/hooks/use-load-file-query';
import { LightSourceLabel } from '#apps/client-web/features/audit-form-lighting-type/constants';

import { isNil } from '#packages/data-collection/libs/lodash';
import { type AuditId } from '#packages/data-collection/models/audit';
import {
  AuditLightingCountFormSchema,
  type AuditLightingCountForm,
  type AuditLightingCountFormEntry,
} from '#packages/data-collection/models/audit-lighting-count-form';
import {
  type AuditLightingTypeForm,
  type AuditLightingTypeFormEntry,
  type LightSource,
} from '#packages/data-collection/models/audit-lighting-type-form';
import { type LoggingContext } from '#packages/data-collection/types/logging-context';
import {
  type QueryPageState,
  type MutationPageState,
} from '#packages/data-collection/types/page-state';
import { useDialog } from '#packages/web/hooks/use-dialog';
import { useMenu } from '#packages/web/hooks/use-menu';

import { fromFormData, toFormData } from './mappers';
import {
  EditAuditFormLightingCountEntryDataSchema,
  type EditAuditFormLightingCountEntryData,
} from './types';

export type AuditFormLightingCountEntryPageViewModel = ReturnType<
  typeof useAuditFormLightingCountEntryPageViewModel
>;

type AuditFormLightingCountEntryPageViewModelProps = LoggingContext & {
  auditFormService: AuditFormService;
  fileStorageService: FileStorageService;
  queryClient: QueryClient;
};

export const useAuditFormLightingCountEntryPageViewModel = ({
  logger,
  auditFormService,
  fileStorageService,
  queryClient,
}: AuditFormLightingCountEntryPageViewModelProps) => {
  const goBack = useNavigateBack({ logger });
  const navigate = useNavigate();

  const { auditId, index: indexAsString } = useParams<{
    auditId: AuditId;
    index: string;
  }>();
  const index = parseInt(indexAsString!);

  const auditFormQuery = useLoadAuditFormByType<AuditLightingCountForm>({
    auditFormService,
    auditId: auditId!,
    formType: 'lighting-count',
    queryClient,
  });

  const lightingFormQuery = useLoadAuditFormByType<AuditLightingTypeForm>({
    auditFormService,
    auditId: auditId!,
    formType: 'lighting-type',
    queryClient,
  });

  // Collect first-attachment file IDs for thumbnails in the picker
  const thumbnailFileIds = useMemo(
    () =>
      (lightingFormQuery.data?.entries ?? []).map(
        (entry) => entry.attachments?.[0]?.name ?? null,
      ),
    [lightingFormQuery.data?.entries],
  );

  const uniqueThumbnailFileIds = useMemo(
    () =>
      Array.from(
        new Set(thumbnailFileIds.filter((id): id is string => id !== null)),
      ),
    [thumbnailFileIds],
  );

  const thumbnailQueries = useLoadFilesQuery({
    fileStorageService,
    queryClient,
    fileIds: uniqueThumbnailFileIds,
  });

  const thumbnailMap = useMemo(() => {
    const map: Record<string, FileContent | null> = {};
    uniqueThumbnailFileIds.forEach((fileId, i) => {
      map[fileId] = thumbnailQueries[i]?.data ?? null;
    });
    return map;
  }, [uniqueThumbnailFileIds, thumbnailQueries]);

  // Build groups for the fixture type picker (same structure as the lighting page)
  const fixtureTypeGroups = useMemo(() => {
    const entries = lightingFormQuery.data?.entries ?? [];

    const grouped: Record<
      string,
      { entry: AuditLightingTypeFormEntry; globalIndex: number }[]
    > = {};
    entries.forEach((entry, globalIndex) => {
      const key = entry.lightSource ?? 'unspecified';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push({ entry, globalIndex });
    });

    return Object.entries(grouped).map(([lightSource, groupEntries]) => ({
      id: lightSource,
      label:
        lightSource === 'unspecified'
          ? 'Unspecified'
          : (LightSourceLabel[lightSource as LightSource] ?? lightSource),
      entries: groupEntries.map(({ entry, globalIndex }) => ({
        link: routes.audit.getEnergyAuditLightingTypeFormEntryPath(
          auditId!,
          globalIndex,
        ),
        entry,
        thumbnail: (() => {
          const fileId = thumbnailFileIds[globalIndex];
          return fileId ? (thumbnailMap[fileId] ?? null) : null;
        })(),
      })),
    }));
  }, [lightingFormQuery.data, auditId, thumbnailFileIds, thumbnailMap]);

  const saveAuditFormMutation =
    useSaveAuditFormMutation<AuditLightingCountForm>({
      auditFormService,
      queryClient,
      auditId: auditId!,
      formType: 'lighting-count',
    });

  const form = useForm({
    resolver: zodResolver(
      EditAuditFormLightingCountEntryDataSchema,
    ) as Resolver<EditAuditFormLightingCountEntryData>,
    reValidateMode: 'onChange',
    defaultValues: {},
  });

  // Holds a path to navigate to after a successful save (instead of goBack)
  const pendingNavigateRef = useRef<string | null>(null);

  const onSave = useCallback(
    (formData: EditAuditFormLightingCountEntryData) => {
      if (loadAuditFormState.state !== 'success') {
        logger?.debug(
          'Cannot update lighting count entry, page is not in success state',
        );
        return;
      }

      logger?.debug({ auditId, formData }, 'Updating lighting count form data');

      const previousDataEncoded = auditFormQuery.data
        ? AuditLightingCountFormSchema.encode(auditFormQuery.data)
        : { type: 'lighting-count' as const, entries: [] };

      const newEntryRaw = fromFormData(formData);

      if (index === -1) {
        previousDataEncoded.entries.push(newEntryRaw);
      } else {
        previousDataEncoded.entries[index] = newEntryRaw;
      }

      const savedIndex =
        index === -1 ? previousDataEncoded.entries.length - 1 : index;

      const auditLightingCountForm =
        AuditLightingCountFormSchema.decode(previousDataEncoded);

      saveAuditFormMutation.mutate(
        { auditId: auditId!, form: auditLightingCountForm },
        {
          onSuccess: (savedForm: AuditLightingCountForm) => {
            logger?.debug(
              { auditId, savedForm },
              'Lighting count form updated successfully',
            );
            queryClient
              .invalidateQueries({
                queryKey: [
                  AUDIT_FORMS_QUERY_KEY,
                  auditId,
                  previousDataEncoded.type,
                ],
              })
              .then(() => {
                logger?.debug('Lighting count form queries invalidated');
                const pendingPath = pendingNavigateRef.current;
                pendingNavigateRef.current = null;
                if (pendingPath) {
                  if (savedIndex !== index) {
                    // Replace the -1 URL in history with the real saved index so
                    // goBack() from nested pages (e.g. lighting type entry) returns
                    // to the correct entry instead of creating another new one.
                    navigate(
                      routes.audit.getEnergyAuditLightingCountFormEntryPath(
                        auditId!,
                        savedIndex,
                      ),
                      { replace: true },
                    );
                  }
                  navigate(pendingPath);
                } else {
                  goBack();
                }
              });
          },
          onError: (err) => {
            pendingNavigateRef.current = null;
            logger?.error({ err }, 'Failed to update lighting count form');
            throw err;
          },
        },
      );
    },
    [
      saveAuditFormMutation.mutate,
      index,
      auditId,
      auditFormQuery.data,
      navigate,
      goBack,
    ],
  );

  const updateMutationState = useMemo<MutationPageState>(() => {
    if (saveAuditFormMutation.isIdle || saveAuditFormMutation.isSuccess) {
      return { state: 'idle' };
    }
    if (saveAuditFormMutation.isPending) {
      return { state: 'pending' };
    }
    if (saveAuditFormMutation.isError) {
      return { state: 'error', error: saveAuditFormMutation.error };
    }
    throw new Error('Unhandled mutation state');
  }, [
    saveAuditFormMutation.isIdle,
    saveAuditFormMutation.isPending,
    saveAuditFormMutation.isSuccess,
    saveAuditFormMutation.isError,
    saveAuditFormMutation.error,
  ]);

  const loadAuditFormState = useMemo<
    QueryPageState<{ form: AuditLightingCountFormEntry | undefined }>
  >(() => {
    if (auditFormQuery.isSuccess) {
      return {
        state: 'success',
        data: { form: auditFormQuery.data?.entries[index] },
      };
    }
    if (auditFormQuery.isLoading || auditFormQuery.isPending) {
      return { state: 'loading' };
    }
    if (auditFormQuery.isError) {
      return { state: 'error', error: auditFormQuery.error };
    }
    throw new Error('Unhandled query state');
  }, [
    auditFormQuery.data,
    auditFormQuery.isSuccess,
    auditFormQuery.isLoading,
    auditFormQuery.isPending,
    auditFormQuery.isError,
    auditFormQuery.error,
    index,
  ]);

  const mainMenu = useMenu();

  const deleteDialog = useDialog<AuditLightingCountFormEntry>();
  const isDeleteDisabled =
    updateMutationState.state === 'pending' ||
    loadAuditFormState.state !== 'success' ||
    loadAuditFormState.data.form == null ||
    index < 0;

  const onDelete = useCallback(
    ({ confirmed }: { confirmed: boolean }) => {
      if (isDeleteDisabled || isNil(auditFormQuery.data)) {
        logger?.debug(
          'Cannot delete lighting count entry, page is not in success state',
        );
        return;
      }

      mainMenu.close();

      if (!confirmed) {
        logger?.debug(
          'Opening lighting count entry deletion confirmation dialog',
        );
        deleteDialog.open(loadAuditFormState.data!.form!);
        return;
      }

      const updatedData = { ...auditFormQuery.data };
      updatedData.entries.splice(index, 1);

      saveAuditFormMutation.mutate(
        { auditId: auditId!, form: updatedData },
        {
          onSuccess: (auditForm) => {
            logger?.debug(
              { auditId, auditForm },
              'Lighting count entry deleted successfully',
            );
            queryClient
              .invalidateQueries({
                queryKey: [AUDIT_FORMS_QUERY_KEY, auditId, updatedData.type],
              })
              .then(() => {
                logger?.debug('Lighting count list queries invalidated');
                goBack();
              })
              .catch((err) => {
                logger?.error(
                  { err },
                  'Failed to invalidate lighting count list queries',
                );
                throw err;
              });
          },
          onError: (err) => {
            logger?.error(
              { err, auditId },
              'Failed to delete lighting count entry',
            );
            throw err;
          },
        },
      );
    },
    [auditId, loadAuditFormState, saveAuditFormMutation],
  );

  useEffect(() => {
    if (loadAuditFormState.state !== 'success') {
      return;
    }

    logger?.debug(
      { lightingCountEntry: auditFormQuery.data?.entries[index] },
      'Lighting count form entry default values',
    );

    form.reset(toFormData(loadAuditFormState.data.form));
  }, [auditFormQuery.data?.entries, auditFormQuery.isSuccess]);

  // --- Fixture type picker ---

  const fixtureTypePickerDialog = useDialog();
  const saveChangesConfirmDialog = useDialog<{ navigateTo: string }>();

  const watchedFixtureTypeId = form.watch('fixtureType');
  const selectedFixtureTypeEntry = useMemo(
    () =>
      watchedFixtureTypeId === undefined
        ? undefined
        : lightingFormQuery.data?.entries.find(
            (e) => e.id === watchedFixtureTypeId,
          ),
    [watchedFixtureTypeId, lightingFormQuery.data?.entries],
  );

  const onSelectFixtureType = useCallback(
    (entry: AuditLightingTypeFormEntry) => {
      form.setValue('fixtureType', entry.id, { shouldDirty: true });
      fixtureTypePickerDialog.close();
    },
    [form, fixtureTypePickerDialog],
  );

  const onRequestNavigate = useCallback(
    (navigateTo: string) => {
      if (form.formState.isDirty) {
        saveChangesConfirmDialog.open({ navigateTo });
      } else {
        navigate(navigateTo);
      }
    },
    [form.formState.isDirty, navigate, saveChangesConfirmDialog],
  );

  const onSaveAndNavigate = useCallback(() => {
    const navigateTo = saveChangesConfirmDialog.data?.navigateTo;
    if (!navigateTo) return;
    pendingNavigateRef.current = navigateTo;
    saveChangesConfirmDialog.close();
    fixtureTypePickerDialog.close();
    form.handleSubmit(onSave)();
  }, [saveChangesConfirmDialog, fixtureTypePickerDialog, form, onSave]);

  const onRequestAddFixtureType = useCallback(() => {
    const addLink = routes.audit.getEnergyAuditLightingTypeFormEntryPath(
      auditId!,
      -1,
    );
    onRequestNavigate(addLink);
  }, [auditId, onRequestNavigate]);

  return {
    mainMenu,

    auditFormQuery,
    loadAuditFormState,

    form,
    onSubmit: form.handleSubmit(onSave),
    updateMutationState,

    onDelete,
    deleteDialog,
    isDeleteDisabled,

    fixtureTypeGroups,
    selectedFixtureTypeEntry,
    fixtureTypePickerDialog,
    onSelectFixtureType,
    onRequestNavigate,
    onRequestAddFixtureType,

    saveChangesConfirmDialog,
    onSaveAndNavigate,

    auditId,
  };
};
